// Корень приложения (п.20, финал переезда 2026-09-07): вход, боковое меню,
// выбор проекта, роли и показ текущего экрана. Раньше это была оболочка
// index.html; теперь она — только стили и контейнер.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { alertBox, ask, confirmBox } from './dialog';
import { call, type Data } from './api';
import { filterStore, matchers, type Matchers } from './filter';
import type { ShellCtx } from './shell';
import { Login } from './screens/Login';
import { Menu, TABS, type TabId } from './screens/Menu';
import { WorkCard } from './WorkCard';
import { FloorCard } from './FloorCard';
import { Filters } from './screens/Filters';
import { Model } from './screens/Model';
import { Obshchee } from './screens/Obshchee';
import { Spiski } from './screens/Spiski';
import { Struct } from './screens/Struct';
import { Works } from './screens/Works';
import { Podryad } from './screens/Podryad';
import { Shemy } from './screens/Shemy';
import { Links } from './screens/Links';
import { Users } from './screens/Users';
import { Fronts } from './screens/Fronts';
import { Fakt } from './screens/Fakt';
import { FaktLog } from './screens/FaktLog';
import { Kvart } from './screens/Kvart';
import { Resurs } from './screens/Resurs';
import { Rep } from './screens/Rep';
import { Checks } from './screens/Checks';
import { Wiki } from './screens/Wiki';
import { ZamechanieKnopka, ZamechanieProsmotr } from './Zamechanie';

type Me = Data<'/api/auth/me', 'get'>;
type Project = Data<'/api/projects', 'get'>[number];

const SCREEN: Record<TabId, (ctx: ShellCtx) => JSX.Element> = {
  obshchee: c => <Obshchee ctx={c} />,
  plan: c => <Model ctx={c} />,
  // «Ручное планирование» — та же таблица и шапка, что «Модель ГПР»,
  // только строки — задачи ручных работ плюс строка быстрого ввода (2026-09-08)
  ruch: c => <Model ctx={c} ruch />,
  checks: c => <Checks ctx={c} />, struct: c => <Struct ctx={c} />, works: c => <Works ctx={c} />,
  podryad: c => <Podryad ctx={c} />,
  shemy: c => <Shemy ctx={c} />,
  links: c => <Links ctx={c} />, spiski: c => <Spiski ctx={c} />, resurs: c => <Resurs ctx={c} />,
  kvart: c => <Kvart ctx={c} />, fronty: c => <Fronts ctx={c} />,
  fakt: c => <Fakt ctx={c} />, faktlog: c => <FaktLog ctx={c} />,
  rep: c => <Rep ctx={c} />, users: c => <Users ctx={c} />, wiki: () => <Wiki />,
};
/** на этих экранах панель фильтров не действует */
const NO_FILTERS: TabId[] = ['wiki', 'users', 'obshchee', 'shemy'];
/** «Этажи»: из всей панели нужен только выбор корпусов (владелец 2026-09-09) */
const ONLY_KORPUSA: TabId[] = ['struct'];
/** «Работы»: без корпусов, этажа и подрядчика; место, этап, группа — кнопками (владелец 2026-09-15) */
const DLYA_RABOT: TabId[] = ['works'];
/** «Модель ГПР»: поле работы — в шапке таблицы, а не в панели (владелец 2026-09-15) */
const BEZ_RABOTY: TabId[] = ['plan'];

function readTab(): TabId {
  try {
    const t = localStorage.getItem('plot_tab') as TabId | null;
    return t && TABS.some(x => x.id === t) ? t : 'plan';
  } catch { return 'plan'; }
}
function readPid(): number | null {
  try { return parseInt(localStorage.getItem('plot_pid') || '', 10) || null; } catch { return null; }
}

export function App() {
  const [token, setToken] = useState<string | null>(() => {
    try { return localStorage.getItem('plot_t'); } catch { return null; }
  });
  const [me, setMe] = useState<Me | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [pid, setPid] = useState<number | null>(readPid);
  const [tab, setTab] = useState<TabId>(readTab);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  const [booted, setBooted] = useState(false);
  const [reload, setReload] = useState(0);
  const [fm, setFm] = useState<Matchers>(() => matchers());
  const [cardWork, setCardWork] = useState<string | null>(null);   // карточка работы
  const [cardTab, setCardTab] = useState<'fakt' | undefined>(undefined);   // её вкладка
  const [cardFloor, setCardFloor] = useState<{ korpus: string; etazh: string } | null>(null);  // карточка этажа
  const savedTimer = useRef<number | null>(null);

  // вход: кто я и какие проекты доступны
  const boot = useCallback(async () => {
    if (!token) { setBooted(true); return; }
    const m = await call<Me>('get', '/api/auth/me');
    if (!m.ok) {
      try { localStorage.removeItem('plot_t'); } catch { /* приватный режим */ }
      setToken(null); setBooted(true); return;
    }
    setMe(m.data);
    const pr = await call<Project[]>('get', '/api/projects');
    if (pr.ok) {
      setProjects(pr.data);
      setPid(prev => {
        const ok = prev != null && pr.data.some(p => p.id === prev);
        const next = ok ? prev : (pr.data[0]?.id ?? null);
        try { if (next) localStorage.setItem('plot_pid', String(next)); } catch { /* приватный режим */ }
        return next;
      });
    }
    setBooted(true);
  }, [token]);
  useEffect(() => { void boot(); }, [boot]);

  const project = projects.find(p => p.id === pid) || null;
  const role = me
    ? (me.is_admin ? 'администратор' : ((me.roles || {})[String(pid)]?.role || null))
    : null;
  const canPlan = role === 'администратор' || role === 'планер';
  const isGlobal = project?.code === 'global';

  // роль влияет на оформление: скрытые кнопки правки, админские пункты
  useEffect(() => {
    document.body.classList.toggle('noplan', !canPlan);
    document.body.classList.toggle('isadmin', !!me?.is_admin);
  }, [canPlan, me]);

  useEffect(() => { if (pid) filterStore.setProject(pid); }, [pid]);
  useEffect(() => { try { localStorage.setItem('plot_tab', tab); } catch { /* приватный режим */ } }, [tab]);

  // в «Глобально» доступны только справочники работ, шаблонов связей и списки
  useEffect(() => {
    if (isGlobal && !['works', 'links', 'spiski', 'users', 'wiki'].includes(tab)) setTab('works');
  }, [isGlobal, tab]);

  function flash(msg?: string) {
    setSaved(msg || 'Сохранено.');
    setErr('');
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(''), 8000);
  }

  const ctx: ShellCtx = useMemo(() => ({
    pid: pid ?? 0, role, canPlan,
    korpus: fm.korpusValue(), filterKey: fm.filterKey(), filterActive: fm.active(),
    podr: fm.state.podr, reloadToken: reload,
    matchesWork: fm.matchesWork, matchesKorpus: fm.matchesKorpus, matchesTask: fm.matchesTask,
    onSaved: flash,
    onError: (m: string) => setErr(m),
    onChanged: () => setReload(x => x + 1),
    onRecalc: () => setReload(x => x + 1),
    onAfterRecalc: () => setReload(x => x + 1),
    onWorkCard: (rabota: string, vkladka?: 'fakt') => { setCardTab(vkladka); setCardWork(rabota); },
    onFloorCard: (korpus: string, etazh: string) => setCardFloor({ korpus, etazh }),
    onGoTab: (t: string, korpus?: string) => {
      if (korpus) filterStore.setKorpus(korpus);
      if (TABS.some(x => x.id === t)) setTab(t as TabId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [pid, role, canPlan, fm, reload]);

  const stage = typeof window !== 'undefined' && window.location.port === '8081';
  const stageBar = stage ? (
    <div style={{ background: '#b42318', color: '#fff', textAlign: 'center', fontWeight: 700, padding: '4px 8px' }}>
      ТЕСТОВАЯ КОПИЯ (staging, порт 8081) — данные не боевые, правки здесь на прод не попадают
    </div>) : null;

  if (!booted) return <div className="wrap"><div className="muted">Загрузка…</div></div>;
  if (!token || !me) return <>{stageBar}<Login onDone={t => { setToken(t); setBooted(false); }} /></>;

  if (!projects.length) {
    return (
      <div className="wrap">
        <div className="card">
          <b>{me.name || me.email}</b>
          <div className="muted" style={{ marginTop: 6 }}>
            Вам ещё не выдан доступ ни к одному проекту — обратитесь к администратору.
          </div>
          {me.is_admin && <div className="bar"><NewProject onDone={id => { setPid(id); void boot(); }} /></div>}
          <div className="bar"><button className="sec" onClick={logout}>Выйти</button></div>
        </div>
      </div>
    );
  }

  return (
    <>
    {stageBar}
    <div className="layout" id="appView">
      <Menu
        tab={tab} onTab={setTab} me={me} role={role} isGlobal={isGlobal}
        projects={projects} pid={pid} onProject={id => {
          setPid(id);
          try { localStorage.setItem('plot_pid', String(id)); } catch { /* приватный режим */ }
          filterStore.set({ korpusa: [] });
          setReload(x => x + 1);
        }}
        onNewProject={id => { setPid(id); void boot(); }}
        onLogout={logout}
        onLogoutAll={() => void logoutAll()}
      />
      <main className="main">
        <div className="fwrap" style={{ display: NO_FILTERS.includes(tab) ? 'none' : undefined }}>
          <Filters ctx={{ ...ctx, onFilterChange: setFm }} tolkoKorpusa={ONLY_KORPUSA.includes(tab)}
            dlyaRabot={DLYA_RABOT.includes(tab)} bezRaboty={BEZ_RABOTY.includes(tab)} />
        </div>
        <div className="bar" style={{ margin: '4px 0' }}>
          <span className="err">{err}</span>
          <span id="saved" style={{ display: saved ? '' : 'none' }}>{saved}</span>
        </div>
        {pid ? SCREEN[tab](ctx) : <div className="muted">Выберите проект</div>}
      </main>
      {cardWork && pid && (
        <WorkCard key={cardWork + (cardTab || '')} pid={pid} workName={cardWork} can={canPlan}
          vkladka={cardTab}
          onClose={() => setCardWork(null)}
          onChanged={m => { flash(m); setReload(x => x + 1); }} />
      )}
      {cardFloor && pid && (
        <FloorCard pid={pid} korpus={cardFloor.korpus} etazh={cardFloor.etazh} can={canPlan}
          onClose={() => setCardFloor(null)}
          onChanged={m => { flash(m); setReload(x => x + 1); }} />
      )}
    </div>
    {/* замечания «Сообщить о проблеме» (2026-09-12, #20): кнопка на каждом экране
        и просмотр по ссылке из карточки GitHub (/app/#zamechanie=N) */}
    <ZamechanieKnopka pid={pid} sait="app" ekran={TABS.find(x => x.id === tab)?.label} />
    <ZamechanieProsmotr />
    </>
  );

  function logout() {
    try { localStorage.removeItem('plot_t'); } catch { /* приватный режим */ }
    setToken(null); setMe(null); setProjects([]);
  }

  /** погасить прочие свои входы: ссылка на выгрузку несёт токен в адресе,
   *  и если она куда-то попала — доступ отзывается без смены пароля (2026-09-10) */
  async function logoutAll() {
    if (!await confirmBox('Погасить все свои входы, кроме этого? На других устройствах'
      + ' и в старых ссылках доступ перестанет работать.')) return;
    const r = await call<{ pogasheno: number }>('delete', '/api/auth/sessions');
    await alertBox(r.ok ? 'Погашено входов: ' + r.data.pogasheno : r.error.message);
  }
}

/** создание проекта (администратор) */
function NewProject({ onDone }: { onDone: (id: number) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <button disabled={busy} onClick={async () => {
      const name = (await ask('Название нового проекта:') || '').trim();
      if (!name) return;
      // библиотека-шаблон (#7): работы, связи и списки из «Глобально»
      const iz = await confirmBox('Взять работы, связи и списки из «Глобально»?',
        { podpis: 'Нет — проект будет пустым, работы можно добавить из «Глобально» позже по одной.', ok: 'Взять', opasno: false });
      setBusy(true);
      const r = await call<{ id: number }>('post', '/api/projects', { name, iz_globalno: iz });
      setBusy(false);
      if (!r.ok) { await alertBox(r.error.message); return; }
      try { localStorage.setItem('plot_pid', String(r.data.id)); } catch { /* приватный режим */ }
      onDone(r.data.id);
    }}>Создать проект</button>
  );
}
